<template>
  <div class="card">
    <div class="card-body">
      <!-- search input -->
      <div class="custom-search d-flex justify-content">
        <div>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            @click="$router.push({ name: 'user-page', params: { id: 'new' } })"
          >
            Новый пользователь
          </b-button>
        </div>
        <div class="search-container">
          <b-form-group>
            <div class="d-flex align-items-center">
              <label class="mr-1">Имя</label>
              <b-form-input
                v-model="searchByName"
                placeholder="Поиск по имени"
                type="text"
                class="d-inline-block"
              />
            </div>
          </b-form-group>
          <b-form-group>
            <div class="d-flex align-items-center">
              <label class="mr-1">Фамилия</label>
              <b-form-input
                v-model="searchBySurname"
                placeholder="Поиск по фамилии"
                type="text"
                class="d-inline-block"
              />
            </div>
          </b-form-group>
          <b-form-group>
            <div class="d-flex align-items-center">
              <label class="mr-1">Почта</label>
              <b-form-input
                v-model="searchByEmail"
                placeholder="Поиск по почте"
                type="text"
                class="d-inline-block"
              />
            </div>
          </b-form-group>
        </div>
      </div>

      <vue-good-table
        :columns="columns"
        :rows="rows"
        :select-options="{
          enabled: true,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
        }"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <!-- Column: Status -->
          <span v-if="props.column.field === 'status'">
            <b-badge :variant="statusVariant(props.row.status)">
              {{ props.row.status }}
            </b-badge>
          </span>

          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <router-link
                  :to="{ name: 'user-page', params: { id: props.row.id } }"
                  class="edit-button"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    class="mr-50"
                  />
                  <span>Редактировать</span>
                </router-link>

                <b-dropdown-item
                  variant="outline-primary"
                  @click="signAsUser(props.row.id)"
                >
                  <feather-icon
                    icon="ArrowRightIcon"
                    class="mr-50"
                  />
                  <span>Зайти как пользователь</span>
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-modal.modal-primary
                  variant="outline-primary"
                  @click="userToDelete = props.row.id"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Удалить</span>
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
      <infinite-loading
        spinner="bubbles"
        @infinite="infiniteScroll"
      >
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </div>

    <div>
      <div class="demo-inline-spacing" />
      <b-modal
        id="modal-primary"
        v-model="modalIsOpen"
        ok-only
        ok-title="Отмена"
        modal-class="modal-primary"
        centered
        title="Primary Modal"
      >
        <div>Вы действительно хотите удалить пользователя?</div>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          class="mt-1"
          variant="primary"
          @click="deleteItem(userToDelete)"
        >
          Удалить
        </b-button>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import InfiniteLoading from 'vue-infinite-loading'
import config from '@core/app-config/api-config'
import crudModule from '../../libs/crud_module'
import useJwt from '@/auth/jwt/useJwt'

const usersModule = crudModule({
  entity_name: 'user',
})

export default {
  components: {
    VueGoodTable,
    BBadge,
    BFormGroup,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BButton,
    InfiniteLoading,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      skipCount: 0,
      maxResultCount: 10,
      modalIsOpen: false,
      userToDelete: null,
      columns: [
        {
          label: 'Имя пользователя',
          field: 'name',
        },
        {
          label: 'Фамилия',
          field: 'surname',
        },
        {
          label: 'Почта',
          field: 'email',
        },
        {
          label: 'Дата',
          field: this.formatDate,
        },
        {
          label: 'Действие',
          field: 'action',
        },
      ],
      rows: [],
      pageLength: 15,
      dir: false,
      searchByName: '',
      searchByEmail: '',
      searchBySurname: '',
      status: [
        {
          1: 'Current',
          2: 'Professional',
          3: 'Rejected',
          4: 'Resigned',
          5: 'Applied',
        },
        {
          1: 'light-primary',
          2: 'light-success',
          3: 'light-danger',
          4: 'light-warning',
          5: 'light-info',
        },
      ],
    }
  },
  computed: {
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Current: 'light-primary',
        Professional: 'light-success',
        Rejected: 'light-danger',
        Resigned: 'light-warning',
        Applied: 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
  },
  watch: {
    searchByName() {
      this.skipCount = 0
      this.findSearchedUsers()
    },
    searchByEmail() {
      this.skipCount = 0
      this.findSearchedUsers()
    },
    searchBySurname() {
      this.skipCount = 0
      this.findSearchedUsers()
    },
  },
  async created() {
    try {
      const payload = {
        skipCount: '',
        maxResultCount: this.maxResultCount,
      }
      const response = await usersModule.fetchItems(payload)
      this.rows = response.data.rows
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    async signAsUser(id) {
      const token = await useJwt
        .getUserConnectionToken(id)
        .then(res => res.data)
      window.open(
        `${config.clientSiteHost}/sign-as-user?token=${token}`,
        '_blank',
      )
    },
    async infiniteScroll($state) {
      const payload = {
        name: this.searchByName,
        email: this.searchByEmail,
        surname: this.searchBySurname,
        skipCount: this.skipCount,
        maxResultCount: this.maxResultCount,
      }
      await usersModule
        .fetchItems(payload)
        .then(res => {
          if (res.data.rows.length > 0) {
            this.rows.push(...res.data.rows)
            this.skipCount += this.maxResultCount
            $state.loaded()
          } else {
            $state.complete()
          }
        })
        .catch(err => {
          console.log(err)
        })
    },
    async deleteItem(id) {
      const payload = {
        skipCount: this.skipCount,
        maxResultCount: this.maxResultCount,
      }
      await usersModule.deleteItem(id)
      await usersModule.fetchItems(payload).then(response => {
        this.rows = response.data.rows
      })
      this.modalIsOpen = false
    },
    formatDate(rowObj) {
      const newDate = new Date(rowObj.createdAt)
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
      return newDate.toLocaleDateString('ru', options)
    },
    async findSearchedUsers() {
      try {
        const payload = {
          name: this.searchByName,
          email: this.searchByEmail,
          surname: this.searchBySurname,
          skipCount: 0,
          maxResultCount: this.maxResultCount,
        }
        const response = await usersModule.fetchItems(payload)
        this.rows = response.data.rows
        this.skipCount = 10
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-search {
  justify-content: space-between;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>

<style lang="scss">
.edit-button {
  padding: 0.65rem 1.28rem;
  display: flex;
  align-items: center;

  &:hover {
    background-color: rgba(115, 103, 240, 0.12);
  }
}

@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
